/**
 * CollectionSettings Component
 *
 * Manages processing configuration for a knowledge collection.
 * Includes tabs for Chunking, Embedding, Enrichment, and Actions.
 */

import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@/i18n';
import { AlertTriangle, RefreshCw, Tag } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useModelsByCategory } from '@/features/agents/hooks/useAvailableModels';

// ============================================================================
// Types
// ============================================================================

export interface ProcessingConfig {
  chunking: {
    strategy: 'semantic_llm' | 'semantic_heuristic' | 'fixed';
    min_tokens: number;
    max_tokens: number;
    target_tokens: number;
    overlap_tokens: number;
  };
  embedding: {
    model: string;
    dimensions: number;
  };
  enrichment: {
    enrich_with_llm: boolean;
    extract_questions: boolean;
    auto_classify: boolean;
  };
}

export interface CollectionSettingsProps {
  collectionId: string;
  mode: 'rag' | 'full_document';
  config: ProcessingConfig;
  activeEmbeddingModel: string;
  embeddingLocked: boolean;
  documentCount: number;
  onSave: (config: Partial<ProcessingConfig>) => Promise<void>;
  onReprocess: () => Promise<void>;
  onReclassify: () => Promise<void>;
}

// ============================================================================
// Constants
// ============================================================================

const CHUNKING_STRATEGIES = [
  {
    value: 'semantic_llm',
    label: 'Semantic (LLM)',
    description: 'Best quality, uses LLM',
  },
  {
    value: 'semantic_heuristic',
    label: 'Semantic (Heuristic)',
    description: 'Good quality, faster',
  },
  { value: 'fixed', label: 'Fixed Size', description: 'Simple token-based' },
];

// Fallback embedding models when API is unavailable
const FALLBACK_EMBEDDING_MODELS = [
  { value: 'voyage-3', label: 'Voyage 3', dimensions: 1024, description: 'Best quality' },
  {
    value: 'voyage-3-lite',
    label: 'Voyage 3 Lite',
    dimensions: 512,
    description: 'Faster, lighter',
  },
  {
    value: 'voyage-code-2',
    label: 'Voyage Code 2',
    dimensions: 1536,
    description: 'Code optimized',
  },
];

// ============================================================================
// Component
// ============================================================================

export function CollectionSettings({
  collectionId: _collectionId,
  mode,
  config,
  activeEmbeddingModel,
  embeddingLocked: _embeddingLocked,
  documentCount,
  onSave,
  onReprocess,
  onReclassify,
}: CollectionSettingsProps) {
  const { t } = useI18n();
  const [localConfig, setLocalConfig] = useState(config);
  const [saving, setSaving] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);
  const isFullDocument = mode === 'full_document';

  useEffect(() => {
    setLocalConfig(config);
  }, [config]);

  // Fetch embedding models from API (source of truth: PlatformModel.deployment_config)
  const { grouped: modelsByCategory } = useModelsByCategory();

  const embeddingModels = useMemo(() => {
    const apiModels = modelsByCategory.embedding;
    if (apiModels && apiModels.length > 0) {
      return apiModels.map((m) => ({
        value: m.model_id,
        label: m.display_name,
        dimensions: (m.deployment_config?.dimensions as number) || 1024,
        description: m.provider_name || '',
      }));
    }
    return FALLBACK_EMBEDDING_MODELS;
  }, [modelsByCategory.embedding]);

  const hasEmbeddingDrift =
    !isFullDocument &&
    documentCount > 0 && activeEmbeddingModel !== localConfig.embedding.model;
  const hasUnsavedEmbeddingSelection =
    !isFullDocument &&
    localConfig.embedding.model !== config.embedding.model ||
    localConfig.embedding.dimensions !== config.embedding.dimensions;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(
        isFullDocument
          ? {
              enrichment: localConfig.enrichment,
            }
          : {
              chunking: localConfig.chunking,
              embedding: localConfig.embedding,
              enrichment: localConfig.enrichment,
            }
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReprocess = async () => {
    setReprocessing(true);
    try {
      await onReprocess();
    } finally {
      setReprocessing(false);
    }
  };

  const handleReclassify = async () => {
    setReclassifying(true);
    try {
      await onReclassify();
    } finally {
      setReclassifying(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.knowledge.settings.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {hasEmbeddingDrift && (
          <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
            <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600 dark:text-amber-400" />
            <div className="space-y-1 text-sm text-amber-800 dark:text-amber-200">
              <p>
                {hasUnsavedEmbeddingSelection
                  ? t('admin.knowledge.settings.embeddingChangeNeedsSave', {
                      current: activeEmbeddingModel,
                      target: localConfig.embedding.model,
                    })
                  : t('admin.knowledge.settings.embeddingReprocessRequired', {
                      current: activeEmbeddingModel,
                      target: localConfig.embedding.model,
                    })}
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300">
                {t('admin.knowledge.settings.embeddingReprocessHint')}
              </p>
            </div>
          </div>
        )}

        {isFullDocument && (
          <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200">
            {t('admin.knowledge.form.modeFullDocumentDescription')}
          </div>
        )}

        <Tabs defaultValue={isFullDocument ? 'enrichment' : 'chunking'}>
          <TabsList className={`grid w-full ${isFullDocument ? 'grid-cols-2' : 'grid-cols-4'}`}>
            {!isFullDocument && (
              <TabsTrigger value="chunking">
                {t('admin.knowledge.settings.chunkingTab')}
              </TabsTrigger>
            )}
            {!isFullDocument && (
              <TabsTrigger value="embedding">
                {t('admin.knowledge.settings.embeddingTab')}
              </TabsTrigger>
            )}
            <TabsTrigger value="enrichment">
              {t('admin.knowledge.settings.enrichmentTab')}
            </TabsTrigger>
            <TabsTrigger value="actions">
              {t('admin.knowledge.settings.actionsTab')}
            </TabsTrigger>
          </TabsList>

          {/* Tab: Chunking */}
          {!isFullDocument && (
            <TabsContent value="chunking" className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">{t('admin.knowledge.settings.strategy')}</Label>
                <Select
                  value={localConfig.chunking.strategy}
                  onValueChange={(v) =>
                    setLocalConfig({
                      ...localConfig,
                      chunking: {
                        ...localConfig.chunking,
                        strategy: v as ProcessingConfig['chunking']['strategy'],
                      },
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CHUNKING_STRATEGIES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        <div>
                          <div>{s.label}</div>
                          <div className="text-xs text-muted-foreground">{s.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {t('admin.knowledge.settings.targetTokens')}: {localConfig.chunking.target_tokens}
                </Label>
                <Input
                  type="range"
                  value={localConfig.chunking.target_tokens}
                  min={100}
                  max={1000}
                  step={50}
                  onChange={(e) =>
                    setLocalConfig({
                      ...localConfig,
                      chunking: {
                        ...localConfig.chunking,
                        target_tokens: parseInt(e.target.value, 10),
                      },
                    })
                  }
                  className="h-2 cursor-pointer"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>100</span>
                  <span>1000</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {t('admin.knowledge.settings.overlapTokens')}: {localConfig.chunking.overlap_tokens}
                </Label>
                <Input
                  type="range"
                  value={localConfig.chunking.overlap_tokens}
                  min={0}
                  max={200}
                  step={10}
                  onChange={(e) =>
                    setLocalConfig({
                      ...localConfig,
                      chunking: {
                        ...localConfig.chunking,
                        overlap_tokens: parseInt(e.target.value, 10),
                      },
                    })
                  }
                  className="h-2 cursor-pointer"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>0</span>
                  <span>200</span>
                </div>
              </div>

              <div className="flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-900/20">
                <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  {t('admin.knowledge.settings.chunkingWarning')}
                </p>
              </div>
            </TabsContent>
          )}

          {/* Tab: Embedding */}
          {!isFullDocument && (
            <TabsContent value="embedding" className="space-y-4">
              {documentCount > 0 && !hasEmbeddingDrift && (
                <div className="flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-900/20">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    {t('admin.knowledge.settings.chunkingWarning')}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-sm font-medium">{t('admin.knowledge.settings.model')}</Label>
                <Select
                  value={localConfig.embedding.model}
                  onValueChange={(v) =>
                    setLocalConfig({
                      ...localConfig,
                      embedding: {
                        model: v as ProcessingConfig['embedding']['model'],
                        dimensions:
                          embeddingModels.find((m) => m.value === v)?.dimensions || 1024,
                      },
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {embeddingModels.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        <div>
                          <div>{m.label}</div>
                          <div className="text-xs text-muted-foreground">
                            {m.dimensions}d - {m.description}
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="text-sm text-muted-foreground">
                {t('admin.knowledge.settings.dimensions')}: {localConfig.embedding.dimensions}
              </div>
            </TabsContent>
          )}

          {/* Tab: Enrichment */}
          <TabsContent value="enrichment" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">{t('admin.knowledge.settings.enrichWithLlm')}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('admin.knowledge.settings.enrichWithLlmDesc')}
                </p>
              </div>
              <input
                type="checkbox"
                checked={localConfig.enrichment.enrich_with_llm}
                onChange={(e) =>
                  setLocalConfig({
                    ...localConfig,
                    enrichment: {
                      ...localConfig.enrichment,
                      enrich_with_llm: e.target.checked,
                    },
                  })
                }
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">{t('admin.knowledge.settings.extractQuestions')}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('admin.knowledge.settings.extractQuestionsDesc')}
                </p>
              </div>
              <input
                type="checkbox"
                checked={localConfig.enrichment.extract_questions}
                onChange={(e) =>
                  setLocalConfig({
                    ...localConfig,
                    enrichment: {
                      ...localConfig.enrichment,
                      extract_questions: e.target.checked,
                    },
                  })
                }
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">{t('admin.knowledge.settings.autoClassify')}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('admin.knowledge.settings.autoClassifyDesc')}
                </p>
              </div>
              <input
                type="checkbox"
                checked={localConfig.enrichment.auto_classify}
                onChange={(e) =>
                  setLocalConfig({
                    ...localConfig,
                    enrichment: {
                      ...localConfig.enrichment,
                      auto_classify: e.target.checked,
                    },
                  })
                }
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
            </div>
          </TabsContent>

          {/* Tab: Actions */}
          <TabsContent value="actions" className="space-y-4">
            {!isFullDocument && (
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={handleReprocess}
                  disabled={documentCount === 0 || reprocessing}
                >
                  <RefreshCw
                    className={`mr-2 h-4 w-4 ${reprocessing ? 'animate-spin' : ''}`}
                  />
                  {t('admin.knowledge.settings.reprocessAll')} (
                  {documentCount})
                </Button>
                <p className="text-xs text-muted-foreground">
                  {t('admin.knowledge.settings.reprocessDesc')}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={handleReclassify}
                disabled={documentCount === 0 || reclassifying}
              >
                <Tag className={`mr-2 h-4 w-4 ${reclassifying ? 'animate-spin' : ''}`} />
                {t('admin.knowledge.settings.reclassifyAll')} (
                {documentCount})
              </Button>
              <p className="text-xs text-muted-foreground">
                {t('admin.knowledge.settings.reclassifyDesc')}
              </p>
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-6 flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving
              ? t('common.saving')
              : t('common.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default CollectionSettings;
